import { VectorClockComparison, VectorClockComparisonStatus } from './target-types';

/**
 * Increments the device's sequence counter in a version vector.
 */
export function incrementVectorClock(
  clock: Record<string, number> = {},
  deviceId: string
): Record<string, number> {
  const current = clock[deviceId] || 0;
  return {
    ...clock,
    [deviceId]: current + 1,
  };
}

/**
 * Merges two vector clocks component-wise by taking the maximum sequence for each device.
 */
export function mergeVectorClocks(
  c1: Record<string, number> = {},
  c2: Record<string, number> = {}
): Record<string, number> {
  const allKeys = Array.from(new Set([...Object.keys(c1), ...Object.keys(c2)]));
  const merged: Record<string, number> = {};

  for (const key of allKeys) {
    const v1 = c1[key] || 0;
    const v2 = c2[key] || 0;
    merged[key] = Math.max(v1, v2);
  }

  return merged;
}

/**
 * Compares two vector clocks to determine causality:
 * - 'LOCAL_DOMINATES': Local is strictly ahead of remote.
 * - 'REMOTE_DOMINATES': Remote is strictly ahead of local.
 * - 'IDENTICAL': Both clocks have identical sequence counters.
 * - 'CONCURRENT_CONFLICT': Both clocks contain independent divergent updates.
 */
export function compareVectorClocks(
  local: Record<string, number> = {},
  remote: Record<string, number> = {}
): VectorClockComparison {
  const allKeys = Array.from(new Set([...Object.keys(local), ...Object.keys(remote)]));
  let localGreater = false;
  let remoteGreater = false;
  const conflictingKeys: string[] = [];

  for (const k of allKeys) {
    const lVal = local[k] || 0;
    const rVal = remote[k] || 0;

    if (lVal > rVal) {
      localGreater = true;
    } else if (rVal > lVal) {
      remoteGreater = true;
    }
  }

  if (localGreater && remoteGreater) {
    for (const k of allKeys) {
      const lVal = local[k] || 0;
      const rVal = remote[k] || 0;
      if (lVal !== rVal) {
        conflictingKeys.push(k);
      }
    }
    return {
      status: 'CONCURRENT_CONFLICT',
      localClock: { ...local },
      remoteClock: { ...remote },
      conflictingKeys,
    };
  }

  if (localGreater && !remoteGreater) {
    return {
      status: 'LOCAL_DOMINATES',
      localClock: { ...local },
      remoteClock: { ...remote },
    };
  }

  if (remoteGreater && !localGreater) {
    return {
      status: 'REMOTE_DOMINATES',
      localClock: { ...local },
      remoteClock: { ...remote },
    };
  }

  return {
    status: 'IDENTICAL',
    localClock: { ...local },
    remoteClock: { ...remote },
  };
}

/**
 * Returns true if c1 is strictly greater than or equal to c2 across all device counters.
 */
export function isVectorDominant(
  c1: Record<string, number> = {},
  c2: Record<string, number> = {}
): boolean {
  const comp = compareVectorClocks(c1, c2);
  return comp.status === 'LOCAL_DOMINATES' || comp.status === 'IDENTICAL';
}
